import { useState } from 'react';
import { Edit, Play, Plus, Search, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Modal from '@/components/ui/Modal';
import Toggle from '@/components/ui/Toggle';
import { TableColumn } from '@/types';
import { ChannelAutomation } from '@/services/channelAutomationService';
import {
  useChannelAutomationsQuery,
  useChannelAutomationRuns,
  useDeleteChannelAutomation,
  useRunChannelAutomation,
  useToggleChannelAutomationStatus,
} from '@/hooks/useChannelAutomations';

const statusClass = (status?: string | null) => {
  if (status === 'SUCCESS') return 'bg-green-100 text-green-800';
  if (status === 'FAILED') return 'bg-red-100 text-red-800';
  if (status === 'PARTIAL_SUCCESS') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-700';
};

const formatExecution = (cronExpression: string) => {
  const parts = cronExpression.split(' ');
  if (parts.length !== 6) {
    return cronExpression;
  }

  if (parts[1]?.startsWith('*/')) {
    const interval = parts[1].slice(2);
    return `Cada ${interval} minuto${interval === '1' ? '' : 's'}`;
  }

  if (parts[2]?.startsWith('*/')) {
    const interval = parts[2].slice(2);
    return `Cada ${interval} hora${interval === '1' ? '' : 's'}`;
  }

  if (parts[3]?.startsWith('*/')) {
    const interval = parts[3].slice(2);
    return `Cada ${interval} día${interval === '1' ? '' : 's'}`;
  }

  if (parts[0] === '0' && parts[3] === '*' && parts[4] === '*' && parts[5] === '*') {
    const hour = String(Number(parts[2] ?? 0)).padStart(2, '0');
    const minute = String(Number(parts[1] ?? 0)).padStart(2, '0');
    return `Diariamente a las ${hour}:${minute}`;
  }

  return cronExpression;
};

const TVChannelAutomations = () => {
  const navigate = useNavigate();
  const deleteMutation = useDeleteChannelAutomation();
  const runMutation = useRunChannelAutomation();
  const toggleStatusMutation = useToggleChannelAutomationStatus();

  const [selectedAutomation, setSelectedAutomation] = useState<ChannelAutomation | null>(null);
  const [runningAutomationIds, setRunningAutomationIds] = useState<string[]>([]);
  const { data: selectedRuns = [] } = useChannelAutomationRuns(selectedAutomation?.id);

  const handleRunAutomation = async (automationId: string) => {
    if (runningAutomationIds.includes(automationId)) {
      return;
    }

    setRunningAutomationIds((current) => [...current, automationId]);
    try {
      await runMutation.mutateAsync(automationId);
    } finally {
      setRunningAutomationIds((current) => current.filter((id) => id !== automationId));
    }
  };

  const columns: TableColumn<ChannelAutomation>[] = [
    {
      key: 'name',
      sortable: true,
      header: 'Nombre',
      accessor: (automation) => <span className="font-medium text-gray-900">{automation.name}</span>,
    },
    {
      key: 'enabled',
      sortable: true,
      header: 'Estado',
      accessor: (automation) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${automation.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
          {automation.enabled ? 'Activa' : 'Inactiva'}
        </span>
      ),
    },
    {
      key: 'cronExpression',
      sortable: true,
      header: 'Ejecución',
      accessor: (automation) => <span className="text-sm text-gray-900">{formatExecution(automation.cronExpression)}</span>,
    },
    {
      key: 'lastRunAt',
      sortable: true,
      header: 'Última ejecución',
      accessor: (automation) => <span className="text-sm text-gray-900">{automation.lastRunAt ?? 'Nunca'}</span>,
    },
    {
      key: 'lastRunStatus',
      header: 'Último estado',
      accessor: (automation) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass(automation.lastRunStatus)}`}>
          {automation.lastRunStatus ?? 'Sin ejecuciones'}
        </span>
      ),
    },
    {
      key: 'activeToggle',
      header: 'Activa',
      align: 'center',
      className: 'w-16',
      accessor: (automation) => (
        <Toggle
          checked={automation.enabled}
          onChange={(enabled) => toggleStatusMutation.mutate({ automation, enabled })}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'center',
      accessor: (automation) => {
        const isRunning = runningAutomationIds.includes(automation.id);

        return (
          <div className="flex items-center justify-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelectedAutomation(automation)}>
              Runs
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleRunAutomation(automation.id)}
              disabled={isRunning}
              loading={isRunning}
            >
              <Play className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/content/tv-channels/automations/${automation.id}/edit`)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => window.confirm(`¿Eliminar automatización ${automation.name}?`) && deleteMutation.mutate(automation.id)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
      className: 'w-52',
    },
  ];

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        <DataTableWithQuery
          showTotalCount
          tableId="tv-channel-automations"
          columns={columns}
          useQueryHook={useChannelAutomationsQuery}
          tableStyle="compact"
          className="shadow"
          searchPlaceholder="Buscar automatizaciones"
          title="Automatizaciones"
          itemsPerPage={25}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Automatizaciones</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button size="sm" onClick={() => navigate('/content/tv-channels/automations/create')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Plus className="mr-1 h-4 w-4" />
                    Nueva
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={onSearchChange}
                    placeholder={searchPlaceholder}
                    className="h-9 w-full rounded-md border border-gray-300 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">Automatizaciones</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative min-w-[220px] flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={onSearchChange}
                    placeholder={searchPlaceholder}
                    className="h-9 w-full rounded-md border border-gray-300 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-auto">
                  <Button size="sm" onClick={() => navigate('/content/tv-channels/automations/create')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Plus className="mr-1 h-4 w-4" />
                    Nueva automatización
                  </Button>
                </div>
              </div>
            </div>
          )}
        />

        <Modal
          isOpen={Boolean(selectedAutomation)}
          onClose={() => setSelectedAutomation(null)}
          title={selectedAutomation ? `Ejecuciones: ${selectedAutomation.name}` : 'Ejecuciones'}
          size="2xl"
        >
          <div className="space-y-2">
            {selectedRuns.map((run) => (
              <div key={run.id} className="rounded border border-gray-200 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(run.status)}`}>{run.status}</span>
                  <span>{run.triggerType}</span>
                  <span>{run.startedAt}</span>
                  <span>matches: {run.matchedCount}</span>
                  <span>missing: {run.missingCount}</span>
                  <span>channels: {run.channelsUpdatedCount}</span>
                </div>
                {run.errorSummary && <div className="mt-1 text-red-700">{run.errorSummary}</div>}
              </div>
            ))}
            {!selectedRuns.length && <div className="text-sm text-gray-500">Sin ejecuciones.</div>}
          </div>
        </Modal>

      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelAutomations;
